<template>
  <section class="event-detail-page h-full w-full flex flex-col">
    <!-- Hero Section -->
    <div :style="backgroundStyle" class="event-hero relative w-full overflow-hidden">
      <div class="hero-overlay"></div>
      <div class="hero-content">
        <div class="container mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
          <div class="max-w-3xl">
            <div class="hero-badge">
              <span>Event Details</span>
            </div>
            <h1 v-if="getEvent && getEvent.title" class="hero-title">{{ getEvent.title }}</h1>
            <h1 v-else class="hero-title">Loading...</h1>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="event-content bg-gradient-to-b from-gray-50 to-white pt-4 pb-12 sm:pt-6 sm:pb-16">
      <div class="container mx-auto px-4 sm:px-6 lg:px-8">
        <!-- Breadcrumb -->
        <div class="mb-4">
          <bread-crumb :items="breadCrumbItems"></bread-crumb>
        </div>

        <!-- Loading State -->
        <div v-if="$apollo.queries.eventDetails.loading" class="loading-container">
          <div class="loading-header">
            <div class="loading-bar"></div>
          </div>
          <div class="loading-content">
            <div class="loading-image"></div>
            <div class="loading-title"></div>
            <div class="loading-meta"></div>
            <div class="loading-description"></div>
            <div class="loading-text-lines">
              <div v-for="(i, index) in 8" :key="index" class="loading-line"></div>
            </div>
          </div>
        </div>

        <!-- Event Content -->
        <div v-else-if="getEvent" class="event-article">
          <!-- Section Header -->
          <div class="section-header mb-6">
            <div class="flex items-center gap-4">
              <button @click.stop="$router.go(-1)" class="back-button-icon">
                <icon-back class="icon"></icon-back>
              </button>
              <div>
                <h2 class="section-title">{{ getEvent.title }}</h2>
              </div>
            </div>
          </div>

          <!-- Event Meta -->
          <div class="event-meta mb-8">
            <div class="meta-item">
              <svg class="meta-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z">
                </path>
              </svg>
              <span class="meta-text">{{ eventDate }}</span>
            </div>
            <div v-if="getEvent.display_time" class="meta-item">
              <svg class="meta-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <span class="meta-text">{{ startTime }} - {{ endTime }}</span>
            </div>
            <div v-if="getEvent.entity && getEntityImage(getEvent.entity)" class="meta-item entity-badge">
              <img class="entity-logo" :src="getEntityImage(getEvent.entity)" :alt="getEvent.entity.entity || 'Entity'">
              <span class="meta-text">{{ mapEntityName(getEvent.entity.entity) }}</span>
            </div>
          </div>

          <!-- Thumbnail Image -->
          <div v-if="hasThumbnail" class="event-thumbnail mb-8">
            <div class="thumbnail-wrapper">
              <img :src="getImage" :alt="getEvent.title" class="thumbnail-image">
              <div v-if="getEvent.entity && getEntityImage(getEvent.entity)" class="thumbnail-entity-badge">
                <img class="entity-logo-small" :src="getEntityImage(getEvent.entity)"
                  :alt="mapEntityName(getEvent.entity.entity)">
              </div>
            </div>
          </div>

          <!-- Main Content Area -->
          <div class="event-main-content">
            <div class="content-grid">
              <!-- Left Column - Main Content -->
              <div class="content-main">
                <!-- About Event -->
                <div v-if="getEvent.content" class="content-card">
                  <h3 class="content-card-title">About the Event</h3>
                  <div
                    class="content-card-body prose-content"
                    v-html="formattedEventContent"
                  ></div>
                </div>

                <!-- Location -->
                <div v-if="getEvent.location" class="content-card" :class="getEvent.content ? 'mt-6' : ''">
                  <h3 class="content-card-title">Location</h3>
                  <div class="content-card-body">
                    <div class="location-info">
                      <svg class="location-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z">
                        </path>
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                      </svg>
                      <p class="location-text">{{ getEvent.location }}</p>
                    </div>
                  </div>
                </div>

                <!-- Participating Employers -->
                <div
                  v-if="getEvent.event_type == 'Virtual Career Fair' && getEvent.event_employers && getEvent.event_employers.length > 0"
                  class="content-card" :class="getEvent.content || getEvent.location ? 'mt-6' : ''">
                  <h3 class="content-card-title">Participating Employers</h3>
                  <div class="employers-grid">
                    <div v-for="(employer, index) in getEvent.event_employers" :key="index" class="employer-card">
                      <div class="employer-number">{{ index + 1 }}</div>
                      <div class="employer-logo-wrapper">
                        <img class="employer-logo" :src="getCompanyLogo(employer.partner.company_logo)"
                          :alt="employer.partner.en_company_name" />
                      </div>
                      <div class="employer-name">{{ employer.partner.en_company_name }}</div>
                    </div>
                  </div>
                </div>

                <!-- Register Button -->
                <div v-if="getEvent.is_show_register_now_button" class="register-button-wrapper">
                  <button @click.stop="registerEvent" class="register-button" :disabled="isLoading">
                    <span v-if="!isLoading">Register Now</span>
                    <span v-else class="loading-text">
                      <svg class="loading-spinner" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4">
                        </circle>
                        <path class="opacity-75" fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                        </path>
                      </svg>
                      Registering...
                    </span>
                  </button>
                </div>
              </div>

              <!-- Right Column - Sidebar -->
              <div class="content-sidebar">
                <div class="sidebar-card">
                  <div class="sidebar-item" :class="getEvent.display_time || getEvent.focal_point ? 'border-b' : ''">
                    <div class="sidebar-item-label">
                      <svg class="sidebar-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z">
                        </path>
                      </svg>
                      <span>Event Date</span>
                    </div>
                    <div class="sidebar-item-value">{{ eventDate }}</div>
                  </div>
                  <div v-if="getEvent.display_time" class="sidebar-item"
                    :class="getEvent.focal_point ? 'border-b' : ''">
                    <div class="sidebar-item-label">
                      <svg class="sidebar-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                      <span>Event Time</span>
                    </div>
                    <div class="sidebar-item-value">{{ startTime }} - {{ endTime }}</div>
                  </div>
                  <div v-if="getEvent.focal_point" class="sidebar-item">
                    <div class="sidebar-item-label">
                      <svg class="sidebar-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                      </svg>
                      <span>Focal Point</span>
                    </div>
                    <div class="sidebar-item-value">{{ getEvent.focal_point }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading Overlay -->
    <div v-if="isLoading" class="loading-overlay">
      <base-page-spinner type="spin" class-list="h-20"></base-page-spinner>
    </div>

    <!-- Employers Form Modal -->
    <transition name="fade" mode="out-in">
      <base-full-modal v-if="isOpenEmployersForm" :title="`Upload your CV to the participating employers`" height=""
        @close="closeEmployersForm">
        <employers-form :token="getEvent.token" :participating-employers="getEvent.event_employers"
          @close="closeEmployersForm"></employers-form>
      </base-full-modal>
    </transition>
  </section>
</template>

<script>
import gql from "graphql-tag";
import IconBack from "../../components/icons/IconBack.vue";
//import CountDownTimer from '../../components/shared/CountDownTimer.vue';
import BreadCrumb from "../../components/shared/BreadCrumb.vue";
import EmployersForm from "./EmployersForm.vue";
import backgroundImage from '../../assets/images/events/event_banner.png';
import FCHSLogo from '@/assets/images/dashboard/alumni/FCHS.png';
import ADPOLYLogo from '@/assets/images/dashboard/alumni/ADPOLY.png';
import ADVETILogo from '@/assets/images/dashboard/alumni/ADVETI.png';
import ATSLogo from '@/assets/images/dashboard/alumni/ATS.png';
import { applyBidiDirectionToHtml } from "../../helpers/richTextDirection";
export default {
  name: "my-event",
  props: ["id"],
  components: {
    IconBack,
    // CountDownTimer,
    BreadCrumb,
    EmployersForm,
  },
  data() {
    return {
      eventDetails: [],
      breadCrumbItems: [
        { label: "Home", path: "/" },
        { label: "Events", path: "/events" },
        { label: "Event" },
      ],
      isLoading: false,
      isOpenEmployersForm: false,
    };
  },
  computed: {
    backgroundStyle() {
      return {
        'background-image': `url(${backgroundImage})`
      }
    },
    getHeaderText() {
      if (!this.getEvent) return '';
      return this.getEvent.title;
    },
    getEvent() {
      if (!this.eventDetails || !this.eventDetails.data) {
        return null;
      }
      return this.eventDetails.data;
    },
    hasThumbnail() {
      if (!this.getEvent) return false;
      return !!this.getEvent.thumbnail;
    },
    getImage() {
      if (!this.getEvent || !this.getEvent.thumbnail) return '';
      if (typeof this.getEvent.thumbnail === 'string' && (this.getEvent.thumbnail.indexOf('http://') === 0 || this.getEvent.thumbnail.indexOf('https://') === 0)) {
        return this.getEvent.thumbnail;
      }
      return process.env.VUE_APP_PUBLIC_URL + this.getEvent.thumbnail;
    },
    getContent() {
      if (!this.getEvent) return '';
      return this.getEvent.content;
    },
    formattedEventContent() {
      return applyBidiDirectionToHtml(this.getContent);
    },
    eventDate() {
      if (!this.getEvent) return '';
      const start_date = new Date(this.getEvent.start_date);
      const end_date = new Date(this.getEvent.end_date);

      const startMonth = start_date.toLocaleString("default", {
        month: "short",
      });
      const startDate = start_date.getDate();
      const endDay = end_date.getDate();
      const year = start_date.getFullYear();

      let formattedDate = startMonth;

      if (this.getEvent.display_day !== false) {
        if (startDate !== endDay) {
          formattedDate += ` ${startDate}-${endDay}, ${year}`;
        } else {
          formattedDate += ` ${startDate}, ${year}`;
        }
      } else {
        formattedDate += ` - ${year}`;
      }

      return formattedDate;
    },
    startTime() {
      if (!this.getEvent) return '';
      return this.formatTime(this.getEvent.start_date);
    },
    endTime() {
      if (!this.getEvent) return '';
      return this.formatTime(this.getEvent.end_date);
    },
    isLoggedIn() {
      return this.$store.getters.isAuthenticated;
    },
  },
  methods: {
    formatTime(datetime) {
      const date = new Date(datetime);
      const options = { hour: "numeric", minute: "numeric", hour12: true };
      return date.toLocaleTimeString([], options);
    },
    async registerEvent() {
      if (this.isLoggedIn) {
        if (this.getEvent.event_type == 'Virtual Career Fair') {
          this.openEmployersForm();
        } else {
          await this.registerNormalEvent();
        }
      } else {
        this.$swal
          .fire({
            icon: "warning",
            title: "Sorry...",
            text: "You need to be logged in to register for this event.",
          })
          .then((result) => {
            if (result.value || result.dismiss) {
              localStorage.setItem(
                "redirectUrlToFullPath",
                this.$route.fullPath
              );
              this.$router.replace("/login");
            }
          });
      }
    },
    async registerNormalEvent() {
      this.isLoading = true;

      const CREATE_MUTATION = gql`
          mutation RegisterEvent($id: ID!) {
            registerEvent(id: $id) {
              error
              error_code
              message
            }
          }
        `;

      let error_msg = null;
      let success_msg = null;

      try {
        await this.$apollo
          .mutate({
            mutation: CREATE_MUTATION,
            variables: {
              id: parseInt(this.getEvent.id),
            },
          })
          .then((data) => {
            if (data.data.registerEvent.error) {
              error_msg = data.data.registerEvent.message;
            } else {
              success_msg = data.data.registerEvent.message;
            }
          })
          .catch((error) => {
            error_msg = error.message;
          });
      } catch (error) {
        error_msg = error.message || "Please try again later.";
      }

      if (error_msg === null) {
        this.$swal
          .fire({
            icon: "success",
            title: "Great!",
            text: success_msg,
          })
          .then((result) => {
            if (result.value || result.dismiss) {
              if (this.getEvent.registration_type == "external") {
                window.open(this.getEvent.registration_link, "_blank");
              }
            }
          });
      } else {
        this.$swal
          .fire({
            icon: "error",
            title: "Oops...",
            text: error_msg,
          })
          .then((result) => {
            if (result.value || result.dismiss) {
              error_msg = "";
            }
          });
      }

      this.isLoading = false;
    },
    openEmployersForm() {
      this.isOpenEmployersForm = true;
    },
    closeEmployersForm() {
      this.isOpenEmployersForm = false;
    },
    getCompanyLogo(url) {
      if (!url) return '';
      if (typeof url === 'string' && (url.indexOf('http://') === 0 || url.indexOf('https://') === 0)) {
        return url;
      }
      return process.env.VUE_APP_PUBLIC_URL + url;
    },
    getEntityImage(entity) {
      if (!entity || !entity.entity) return '';
      const entityStr = typeof entity.entity === 'string' ? entity.entity : '';
      const logos = {
        FCHS: FCHSLogo,
        ADPOLY: ADPOLYLogo,
        ADVETI: ADVETILogo,
        ATS: ATSLogo,
        ATHS: ATSLogo,
      };
      return logos[entityStr] || '';
    },
    mapEntityName(entity) {
      if (!entity) return '';
      const entityStr = typeof entity === 'string' ? entity : (entity.entity || '');
      return entityStr === 'ATHS' ? 'ATS' : entityStr;
    }
  },
  apollo: {
    eventDetails() {
      return {
        query: gql`
          query EventDetails($slug: String!) {
            eventDetails(slug: $slug) {
              data {
                id
                token
                event_type
                title
                description
                content
                thumbnail
                start_date
                end_date
                location
                focal_point
                display_time
                display_day
                is_show_register_now_button
                registration_type
                display_message
                registration_link
                entity {
                  logo
                  entity
                }
                event_employers {
                  id
                  partner {
                    en_company_name
                    company_logo
                  }
                }
              }
            }
          }
        `,
        variables() {
          return {
            slug: this.id,
          };
        },
        fetchPolicy: "cache-and-network",
      };
    },
  },
  watch: {
    getEvent(newVal) {
      if (newVal && newVal.title) {
        this.breadCrumbItems[2].label = newVal.title;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.event-hero {
  height: 400px;
  background-attachment: fixed;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  position: relative;

  @media (min-width: 640px) {
    height: 450px;
  }

  @media (min-width: 1024px) {
    height: 500px;
  }
}

.hero-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(57, 72, 103, 0.85) 0%, rgba(73, 157, 212, 0.75) 100%);
  z-index: 1;
}

.hero-content {
  position: relative;
  z-index: 2;
  height: 100%;
  color: white;
}

.hero-badge {
  display: inline-block;
  margin-bottom: 1rem;
}

.hero-badge span {
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 1rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  color: white;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.hero-title {
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 0;
  line-height: 1.2;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);

  @media (min-width: 640px) {
    font-size: 2.5rem;
  }

  @media (min-width: 1024px) {
    font-size: 3rem;
  }
}

.section-header {
  padding-bottom: 1rem;
  border-bottom: 2px solid #e2e8f0;
}

.back-button-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  background: linear-gradient(135deg, #394867 0%, #499DD4 100%);
  color: white;
  border-radius: 0.5rem;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  flex-shrink: 0;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(57, 72, 103, 0.3);
  }

  .icon {
    width: 1.25rem;
    height: 1.25rem;
  }
}

.section-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 0;
  line-height: 1.3;

  @media (min-width: 640px) {
    font-size: 1.75rem;
  }

  @media (min-width: 1024px) {
    font-size: 2rem;
  }
}

.event-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  padding: 1.5rem;
  background: white;
  border-radius: 0.75rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #64748b;
  font-size: 0.875rem;
  font-weight: 500;

  @media (min-width: 640px) {
    font-size: 0.9375rem;
  }
}

.meta-icon {
  width: 1.125rem;
  height: 1.125rem;
  color: #499DD4;
  flex-shrink: 0;
}

.meta-text {
  color: #475569;
}

.entity-badge {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: #f8fafc;
  border-radius: 0.5rem;
  border: 1px solid #e2e8f0;
}

.entity-logo {
  height: 1.25rem;
  width: auto;
  object-fit: contain;
}

.event-thumbnail {
  border-radius: 1rem;
  overflow: hidden;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.thumbnail-wrapper {
  position: relative;
  width: 100%;
  padding-top: 56.25%; // 16:9 aspect ratio
  background: #f0f4f8;
  overflow: hidden;
}

.thumbnail-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.thumbnail-entity-badge {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.8);
}

.entity-logo-small {
  height: 1.5rem;
  width: auto;
  object-fit: contain;
}

.event-main-content {
  margin-top: 2rem;
}

.content-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;

  @media (min-width: 1024px) {
    grid-template-columns: 2fr 1fr;
  }
}

.content-card {
  background: white;
  padding: 2rem;
  border-radius: 0.75rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
}

.content-card-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #e2e8f0;

  @media (min-width: 640px) {
    font-size: 1.75rem;
  }
}

.content-card-body {
  color: #475569;
  line-height: 1.7;
}

.location-info {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
}

.location-icon {
  width: 1.5rem;
  height: 1.5rem;
  color: #499DD4;
  flex-shrink: 0;
  margin-top: 0.125rem;
}

.location-text {
  font-size: 1.125rem;
  color: #475569;
  line-height: 1.7;
}

.employers-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;

  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }
}

.employer-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem;
  background: #f8fafc;
  border-radius: 0.75rem;
  border: 1px solid #e2e8f0;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    border-color: #499DD4;
  }
}

.employer-number {
  font-size: 1rem;
  font-weight: 600;
  color: #64748b;
  min-width: 1.5rem;
}

.employer-logo-wrapper {
  flex-shrink: 0;
  width: 3.5rem;
  height: 3.5rem;
  background: white;
  border-radius: 0.5rem;
  border: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
}

.employer-logo {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.employer-name {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #1e293b;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.register-button-wrapper {
  margin-top: 2rem;
}

.register-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 1rem 2.5rem;
  background: linear-gradient(135deg, #394867 0%, #499DD4 100%);
  color: white;
  border-radius: 0.75rem;
  font-weight: 600;
  font-size: 1.125rem;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  text-transform: uppercase;
  letter-spacing: 0.05em;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 12px -2px rgba(0, 0, 0, 0.15);
    background: linear-gradient(135deg, #499DD4 0%, #394867 100%);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
}

.loading-spinner {
  width: 1.25rem;
  height: 1.25rem;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

.sidebar-card {
  background: white;
  padding: 1.5rem;
  border-radius: 0.75rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
  position: sticky;
  top: 6rem;
}

.sidebar-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 0;
  border-bottom: 1px solid #e2e8f0;

  &:last-child {
    border-bottom: none;
  }
}

.sidebar-item-label {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #1e293b;
}

.sidebar-icon {
  width: 1.125rem;
  height: 1.125rem;
  color: #499DD4;
  flex-shrink: 0;
}

.sidebar-item-value {
  font-size: 0.875rem;
  color: #64748b;
  text-align: right;
  font-weight: 500;
}

.prose-content {
  max-width: none;
  color: #1e293b;
  line-height: 1.8;
  font-size: 1rem;

  @media (min-width: 640px) {
    font-size: 1.125rem;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-weight: 700;
    margin-top: 1.5em;
    margin-bottom: 0.75em;
    color: #1e293b;
    line-height: 1.3;
  }

  p {
    margin-bottom: 1.5em;
    line-height: 1.8;
  }

  img {
    max-width: 100%;
    height: auto;
    display: block;
    margin: 1.5em auto;
    border-radius: 0.5rem;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  a {
    color: #499DD4;
    text-decoration: underline;
    transition: color 0.2s ease;

    &:hover {
      color: #394867;
    }
  }

  ul,
  ol {
    margin-top: 1em;
    margin-bottom: 1em;
    padding-left: 1.5em;
  }

  ul {
    list-style-type: disc;
  }

  ol {
    list-style-type: decimal;
  }

  li {
    margin-bottom: 0.5em;
    line-height: 1.8;
  }

  blockquote {
    border-left: 4px solid #499DD4;
    padding-left: 1em;
    margin-left: 0;
    margin-right: 0;
    margin-top: 1.5em;
    margin-bottom: 1.5em;
    font-style: italic;
    color: #64748b;
    background: #f8fafc;
    padding: 1em;
    border-radius: 0.5rem;
  }

  .ql-align-center {
    text-align: center;
  }

  .ql-align-right {
    text-align: right;
  }

  .ql-align-justify {
    text-align: justify;
  }

  .ql-direction-rtl {
    direction: rtl;
    text-align: right;
  }

  .ql-direction-rtl.ql-align-center {
    text-align: center;
  }

  .ql-direction-rtl.ql-align-left {
    text-align: left;
  }

  .ql-indent-1 {
    padding-left: 3em;
  }

  .ql-direction-rtl.ql-indent-1 {
    padding-left: 0;
    padding-right: 3em;
  }

  .ql-indent-2 {
    padding-left: 6em;
  }

  .ql-direction-rtl.ql-indent-2 {
    padding-left: 0;
    padding-right: 6em;
  }
}

.prose-content--rtl {
  direction: rtl;
  text-align: right;
  unicode-bidi: embed;

  ul,
  ol {
    padding-left: 0;
    padding-right: 1.5em;
  }

  blockquote {
    border-left: none;
    border-right: 4px solid #499DD4;
  }
}

.prose-content [dir="rtl"] {
  direction: rtl;
  text-align: right;
  unicode-bidi: embed;
}

.prose-content [dir="ltr"] {
  direction: ltr;
  text-align: left;
  unicode-bidi: embed;
}

.prose-content [dir="auto"] {
  unicode-bidi: plaintext;
}

.prose-content ul[dir="rtl"],
.prose-content ol[dir="rtl"] {
  padding-left: 0;
  padding-right: 1.5em;
}

.prose-content ul[dir="ltr"],
.prose-content ol[dir="ltr"] {
  padding-right: 0;
  padding-left: 1.5em;
}

.prose-content li[dir="rtl"] {
  text-align: right;
}

.prose-content li[dir="ltr"] {
  text-align: left;
}

.loading-container {
  background: white;
  border-radius: 0.75rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
  overflow: hidden;
}

.loading-header {
  padding: 1rem 1.5rem;
  background: linear-gradient(90deg, rgba(34, 42, 57, 1) 0%, rgba(10, 66, 117, 1) 100%);
}

.loading-bar {
  height: 0.5rem;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 9999px;
  animation: pulse 1.5s infinite ease-in-out;
}

.loading-content {
  padding: 2rem;
}

.loading-image {
  width: 100%;
  height: 400px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 0.75rem;
  margin-bottom: 2rem;
}

.loading-title {
  height: 2rem;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 0.25rem;
  margin-bottom: 1rem;
  width: 70%;
}

.loading-meta {
  height: 1rem;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 0.25rem;
  margin-bottom: 1.5rem;
  width: 40%;
}

.loading-description {
  height: 1.5rem;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 0.25rem;
  margin-bottom: 2rem;
  width: 100%;
}

.loading-text-lines {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.loading-line {
  height: 1rem;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 0.25rem;

  &:nth-child(1) {
    width: 100%;
  }

  &:nth-child(2) {
    width: 95%;
  }

  &:nth-child(3) {
    width: 100%;
  }

  &:nth-child(4) {
    width: 90%;
  }

  &:nth-child(5) {
    width: 100%;
  }

  &:nth-child(6) {
    width: 85%;
  }

  &:nth-child(7) {
    width: 100%;
  }

  &:nth-child(8) {
    width: 92%;
  }
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }

  100% {
    background-position: 200% 0;
  }
}

.loading-overlay {
  position: fixed;
  inset: 0;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>

<style lang="scss">
strong {
  font-weight: bold;
}

ol {
  list-style: decimal;
  margin-left: 30px;
}

ul {
  list-style: disc;
  margin-left: 30px;
}
</style>
